import { get, includes, size } from 'lodash';
import dayjs from 'dayjs';

// Enums
import AssortmentCanvases from '@enums/assortment-canvases';

// Composables
import { useDayjs } from '@/composables/use-dayjs';

const { FilterOperators } = AssortmentCanvases;

/**
 * Determines whether the given product date value should be filtered out based upon the operator and filter
 * values supplied.
 * @param {Object} RORO - The RORO wrapper
 * @param {String} RORO.productDateValue - The value of the product field to be compared agsint the filterDateValues.
 * This should be supplied either as a string or a dayjs object.
 * @param {FilterOperators} RORO.filterOperator - The operator to apply when filtering.
 * @param {Array<string>} RORO.filterDateValues - The set of date values to filter against. These should be supplied as
 * an array of either strings or dayjs objects.
 * @returns {Boolean|undefined} - Returns true if the product should be filtered out, false if it should be included,
 * and undefined if the product or filter has no value.
 */
function shouldCanvasProductBeFilteredOutByDate({
  productDateValue,
  filterOperator,
  filterDateValues,
}) {
  if (!Object.values(FilterOperators).includes(filterOperator)) {
    throw new Error(`Unsupported filter operator supplied: [${filterOperator}]`);
  }
  // Don't do any filtering when the dates are not provided.
  if (!productDateValue || !size(filterDateValues)) {
    return;
  }

  const {
    checkDateIsSame,
    checkDateIsSameOrBefore,
    checkDateIsSameOrAfter,
    checkDateIsAfter,
    checkDateIsBefore,
  } = useDayjs();

  const DAY = 'day';
  // Some filter types only allow one value to be selected.
  const firstFilterValue = dayjs(filterDateValues[0]);

  switch (filterOperator) {
    case FilterOperators.GreaterThan:
      return checkDateIsAfter(productDateValue, firstFilterValue, DAY);
    case FilterOperators.LessThan:
      return checkDateIsBefore(productDateValue, firstFilterValue, DAY);
    case FilterOperators.GreaterThanOrEqual:
      return checkDateIsSameOrAfter(productDateValue, firstFilterValue, DAY);
    case FilterOperators.LessThanOrEqual:
      return checkDateIsSameOrBefore(productDateValue, firstFilterValue, DAY);
    case FilterOperators.Equal:
      // For equal, users can select multiple filter values which are treated as OR conditions,
      // so we include the product if any value is equal.
      return filterDateValues.some(fv => {
        return checkDateIsSame(productDateValue, fv, DAY);
      });
    case FilterOperators.NotEqual:
      // For not equal, users can select multiple filter values which are treated as OR conditions.
      // We include the product if every filter value is not equal.
      return filterDateValues.every(fv => {
        return !checkDateIsSame(productDateValue, fv, DAY);
      });
    default:
      return undefined;
  }
}

/**
 * Function that will filter an array of items for a provided array of filters.
 * It can check whether values are equal or not equal. This could be extended in future to handle extra operations
 *
 * Example structure of the filters:
 * [
 *    {
 *      filterName: 'Furniture Name'
 *      filterType: furnitureName // This is not used here but is in other places that use filters
 *      filterValues: [{ name: 'Furniture 1', value: 'Furniture 1' }, { name: 'Furniture 2', value: 'Furniture 2' }]
 *      notEqualTo: false, // Would be true if we want to find all furniture names that do not equal the selections above
 *      path: 'furnitureName' // The path to the value in the item object
 *    }
 * ]
 *
 * @param {Object} RORO - The RORO wrapper
 * @param {String} RORO.filters - The array of filters
 * @param {String} RORO.items - The items to be filtered
 */
function applyFiltersToData({ filters, items }) {
  if (!filters || !filters.length) return items;
  return items.filter(item =>
    filters.every(filter => {
      const currentValue = get(item, filter.path, '');
      const filterValues = filter.filterValues.map(({ value }) => value);
      return includes(filterValues, currentValue) !== filter.notEqualTo;
    })
  );
}

export default { applyFiltersToData, shouldCanvasProductBeFilteredOutByDate };
